import {useMemo} from 'react'

import {parseFilterKeywords} from './utils/parse-filter-keywords'
import {filterConversationItems, filterThreads, type ParsedCommentsFilterState} from './utils/filter-items'
import {buildConversationItems, type ConversationItem} from './utils/build-conversation-items'
import {useDiffPathOrderMap, useIssueComments, useReviewSummaries, useThreads} from '../../stores/layout-selectors'
import {sortConversationItemsByDate, sortThreadItemsByDiffOrder} from './utils/sort-items'
import {getConversationItemsAuthors, type AuthorFilterItem} from './utils/get-author-filters'
import {useIssueCommentLiveUpdates} from '../../hooks/use-issue-comment-live-updates'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CommentsSort, isReviewCommentsMode} from './use-comments-sort-state'
import type {CommentsFilterState} from './use-comments-filter-state'

export interface UseFilteredCommentsDataOptions {
  /** Current filter state. */
  filterState: CommentsFilterState
  /** Current sort direction. */
  sort: CommentsSort
}

export interface FilteredCommentsData {
  /** Unique authors of unfiltered items, for the author filter. */
  authorList: AuthorFilterItem[]
  /** Conversation items matching the conversation filter and sort direction. */
  filteredConversationItems: ConversationItem[]
  /** Total number of conversation items in this PR. */
  totalConversationItemsCount: number
}

const emptyItems = {
  unfilteredItems: [],
  filteredSortedItems: [],
}

export function useFilteredCommentsData({filterState, sort}: UseFilteredCommentsDataOptions): FilteredCommentsData {
  const parsedFilterState = useMemo<ParsedCommentsFilterState>(
    () => ({
      ...filterState,
      parsedKeywords: parseFilterKeywords(filterState.filterText),
    }),
    [filterState],
  )

  const threadPreviews = useThreads()

  const diffPathOrderMap = useDiffPathOrderMap()

  const issueComments = useIssueComments()
  useIssueCommentLiveUpdates()

  const reviewSummaries = useReviewSummaries()

  const reviewCommentsMode = isReviewCommentsMode(sort)

  // Diff-order sort displays threads as flat list without issue comments or review summaries
  const diffOrderItems = useMemo(() => {
    if (!reviewCommentsMode) return emptyItems

    const unfilteredItems = buildConversationItems([], [], threadPreviews)
    const filteredSortedItems = sortThreadItemsByDiffOrder(
      filterConversationItems(unfilteredItems, parsedFilterState),
      diffPathOrderMap,
    )

    return {unfilteredItems, filteredSortedItems}
  }, [parsedFilterState, threadPreviews, diffPathOrderMap, reviewCommentsMode])

  // Date-order sort displays full list of comments and reviews with threads
  const dateOrderItems = useMemo(() => {
    if (reviewCommentsMode) return emptyItems

    // Items are built using filtered threads so that we don't show non-matching threads inside matching review items
    const threadsForItems = isFeatureEnabled('prs_docked_comments_replies')
      ? filterThreads(threadPreviews, parsedFilterState)
      : []

    const unfilteredItems = buildConversationItems(issueComments, reviewSummaries, threadsForItems)
    const filteredSortedItems = sortConversationItemsByDate(
      filterConversationItems(unfilteredItems, parsedFilterState),
      sort,
    )

    return {unfilteredItems, filteredSortedItems}
  }, [reviewCommentsMode, threadPreviews, parsedFilterState, issueComments, reviewSummaries, sort])

  // Even though we resolve to a single value, the two separate memos help minimize dependencies per-mode to reduce
  // unnecessary calculations and renders, at a small complexity cost
  const resolvedItems = reviewCommentsMode ? diffOrderItems : dateOrderItems

  const authorList = useMemo(() => {
    const authorCountFilterState = {...parsedFilterState, selectedAuthor: undefined}
    const filteredItemsForAuthorCounts = filterConversationItems(resolvedItems.unfilteredItems, authorCountFilterState)
    return getConversationItemsAuthors(resolvedItems.unfilteredItems, filteredItemsForAuthorCounts)
  }, [parsedFilterState, resolvedItems.unfilteredItems])

  return {
    filteredConversationItems: resolvedItems.filteredSortedItems,
    authorList,
    totalConversationItemsCount: resolvedItems.unfilteredItems?.length ?? 0,
  }
}
